import { ReactNode, useState, useRef, useEffect } from 'react';
import { FieldValues, Path, useFormContext } from 'react-hook-form';
import { X, Check, ChevronDown } from 'lucide-react';
import * as SelectPrimitive from '@radix-ui/react-select';
import LayoutGap from '@/components/Common/LayoutGap';

import FormField from '../helpers/FormField';
import FormItem from '../helpers/FormItem';
import FormLabel from '../helpers/FormLabel';
import FormDescription from '../helpers/FormDescription';
import FormMessage from '../helpers/FormMessage';
import FormControl from '../helpers/FormControl';

export type Option = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type MultiSelectFieldProps<T extends FieldValues> = {
  label?: string;
  name: Path<T>;
  placeholder?: string;
  options: Option[];
  description?: ReactNode;
  className?: string;
  disabled?: boolean;
  onValueChange?: (value: string[]) => void;
};

const MultiSelectFormControl = <T extends FieldValues>({
  label,
  name,
  options,
  description,
  className,
  placeholder = 'Select options...',
  disabled,
  onValueChange,
}: MultiSelectFieldProps<T>) => {
  const form = useFormContext<T>();
  const [isOpen, setIsOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <FormField
      name={name}
      control={form.control}
      render={({ field: { ref, value = [], onChange, ...field } }) => {
        const selectedValues = (Array.isArray(value) ? value : []) as string[];

        return (
          <FormItem className={className}>
            <LayoutGap direction="column" size="small">
              <FormLabel htmlFor={`form-field--${field.name}`}>
                {label}
              </FormLabel>
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap gap-1">
                  {selectedValues.map((selectedValue) => {
                    const option = options.find(
                      (opt) => opt.value === selectedValue,
                    );

                    if (!option) {
                      return null;
                    }

                    return (
                      <div
                        key={selectedValue}
                        className="flex items-center gap-2 rounded bg-neutral-100 px-2 py-1 text-xs"
                      >
                        {option?.label}
                        <button
                          type="button"
                          onClick={() => {
                            const newValues = selectedValues.filter(
                              (v) => v !== selectedValue,
                            );
                            onChange(newValues);
                            onValueChange?.(newValues);
                          }}
                          className="text-neutral-500 hover:text-neutral-700"
                          aria-label={`Remove ${option?.label}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
                <SelectPrimitive.Root
                  value=""
                  onValueChange={(newValue: string) => {
                    if (!selectedValues.includes(newValue)) {
                      const updatedValues = [...selectedValues, newValue];
                      onChange(updatedValues);
                      onValueChange?.(updatedValues);
                    }
                  }}
                  disabled={disabled}
                  open={isOpen}
                  onOpenChange={setIsOpen}
                >
                  <FormControl>
                    <SelectPrimitive.Trigger
                      ref={ref}
                      className="flex h-auto min-h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-neutral-200 bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-neutral-500 focus:border-primary-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <SelectPrimitive.Value placeholder={placeholder} />
                      <SelectPrimitive.Icon asChild>
                        <ChevronDown className="h-4 w-4 opacity-50" />
                      </SelectPrimitive.Icon>
                    </SelectPrimitive.Trigger>
                  </FormControl>
                  <SelectPrimitive.Portal>
                    <SelectPrimitive.Content
                      className="relative z-50 max-h-96 w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border border-neutral-200 bg-white text-neutral-950 shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
                      position="popper"
                      sideOffset={4}
                    >
                      <SelectPrimitive.Viewport className="p-1">
                        {options.map((option) => (
                          <SelectPrimitive.Item
                            key={option.value}
                            value={option.value}
                            disabled={
                              option.disabled ||
                              selectedValues.includes(option.value)
                            }
                            className="relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-neutral-100 focus:text-neutral-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                          >
                            <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                              {selectedValues.includes(option.value) && (
                                <Check className="h-4 w-4" />
                              )}
                            </span>
                            <SelectPrimitive.ItemText>
                              {option.label}
                            </SelectPrimitive.ItemText>
                          </SelectPrimitive.Item>
                        ))}
                      </SelectPrimitive.Viewport>
                    </SelectPrimitive.Content>
                  </SelectPrimitive.Portal>
                </SelectPrimitive.Root>
              </div>
              {description && <FormDescription>{description}</FormDescription>}
              <FormMessage />
            </LayoutGap>
          </FormItem>
        );
      }}
    />
  );
};

MultiSelectFormControl.displayName = 'MultiSelectFormControl';

export default MultiSelectFormControl;
